import {
  Box,
  Button,
  ButtonGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Icon,
  InlineStack,
  Panel,
  PanelSection,
  Text,
  PanelInset,
} from '@customerio/pluma-components/react';
import type { CopilotApprovalComponentProps } from '@customerio/hydra-copilot';
import { useEffect, useMemo, useRef } from 'react';
import {
  analyzeBashCommand,
  containsDeleteRequest,
  getMutatingResources,
  type AnalysisResult,
} from './parse-curl-resources';
import { useApprovedResources } from './approved-resources-context';

// ---------------------------------------------------------------------------
// Internal helpers (never exposed to user)
// ---------------------------------------------------------------------------

function collectMutatingPaths(analysis: AnalysisResult | null): Set<string> {
  if (analysis == null) return new Set();
  return new Set(getMutatingResources(analysis.resources).map((r) => r.path));
}

function collectMutatingTypes(analysis: AnalysisResult | null): string[] {
  if (analysis == null) return [];
  const types = new Set(getMutatingResources(analysis.resources).map((r) => r.resourceType));
  return [...types];
}

function describeAction(planned: string | undefined, hasDelete: boolean): string {
  if (planned != null && planned.length > 0) return planned;
  if (hasDelete) return 'Delete this resource?';
  return 'Make this change?';
}

function buildAlwaysAllowLabel(types: string[]): string {
  if (types.length === 0) return 'Always allow for this';
  const singular = (s: string) => (s.endsWith('s') ? s.slice(0, -1) : s);
  if (types.length === 1) return `Always allow for this ${singular(types[0])}`;
  const last = types[types.length - 1];
  const rest = types.slice(0, -1).join(', ');
  return `Always allow for these ${rest} and ${last}`;
}

function buildAlwaysAllowDescription(types: string[]): string {
  if (types.length === 0) return 'Automatically approve future changes to this resource.';
  const singular = (s: string) => (s.endsWith('s') ? s.slice(0, -1) : s);
  if (types.length === 1)
    return `Automatically approve future changes to this ${singular(types[0])} for the rest of this session.`;
  return 'Automatically approve future changes to these resources for the rest of this session.';
}

type Status = 'pending' | 'approved' | 'denied' | 'error';

function resolveStatus(isPending: boolean, isError: boolean, isRejected: boolean): Status {
  if (isPending) return 'pending';
  if (isError) return 'error';
  if (isRejected) return 'denied';
  return 'approved';
}

const STATUS_ICON: Record<
  Status,
  { name: 'check-circle' | 'stop-circle' | 'error'; color: 'success' | 'subtle' | 'critical' }
> = {
  pending: { name: 'check-circle', color: 'subtle' },
  approved: { name: 'check-circle', color: 'success' },
  denied: { name: 'stop-circle', color: 'subtle' },
  error: { name: 'error', color: 'critical' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ApprovalComponentProps extends CopilotApprovalComponentProps {
  description?: React.ReactNode;
}

export function ApprovalComponent({ toolCall, onApprove, onDeny, description }: ApprovalComponentProps) {
  const isPending = toolCall.status === 'executing';
  const isError = toolCall.status === 'error';
  const decision = toolCall.response?.result as { status: 'approved' | 'rejected' } | undefined;
  const isRejected = !isPending && !isError && decision?.status === 'rejected';
  const status = resolveStatus(isPending, isError, isRejected);

  const planned = toolCall.summary?.planned;
  const { isAllApproved, approveAll, areResourcesApproved, approveResources } = useApprovedResources();

  const analysis = useMemo<AnalysisResult | null>(() => {
    if (toolCall.name !== 'bash') return null;
    const command = toolCall.args?.command;
    if (typeof command !== 'string') return null;
    return analyzeBashCommand(command);
  }, [toolCall.name, toolCall.args?.command]);

  const hasResources = analysis != null && analysis.fullyAnalyzed && analysis.resources.length > 0;
  const hasDelete = analysis != null && containsDeleteRequest(analysis);
  const mutPaths = useMemo(() => collectMutatingPaths(analysis), [analysis]);
  const mutTypes = useMemo(() => collectMutatingTypes(analysis), [analysis]);
  const title = describeAction(planned, hasDelete);
  const showResourceOption = hasResources && !hasDelete && mutPaths.size > 0;

  // Auto-approve (never for DELETE, never for unanalyzable bash commands)
  const isBash = toolCall.name === 'bash';
  const isFullyAnalyzed = analysis != null && analysis.fullyAnalyzed;
  const needsManualApproval = hasDelete || (isBash && !isFullyAnalyzed);

  const didAutoApprove = useRef(false);
  useEffect(() => {
    if (!isPending || didAutoApprove.current || needsManualApproval) return;
    if (isAllApproved) {
      didAutoApprove.current = true;
      onApprove();
      return;
    }
    if (hasResources && mutPaths.size > 0 && areResourcesApproved(mutPaths)) {
      didAutoApprove.current = true;
      onApprove();
    }
  }, [isPending, needsManualApproval, isAllApproved, hasResources, mutPaths, areResourcesApproved, onApprove]);

  // Resolved states — compact inline
  if (status !== 'pending') {
    const icon = STATUS_ICON[status];
    return (
      <Panel>
        <PanelInset>
          <InlineStack alignBlock="center" gap="075" p="150">
            <Icon name={icon.name} color={icon.color} size="sm" />
            <Text size="xs" color={status === 'error' ? 'critical' : 'subtle'}>
              {status === 'error' ? (
                <>
                  <strong>Error:</strong> {title}
                </>
              ) : (
                title
              )}
            </Text>
          </InlineStack>
        </PanelInset>
      </Panel>
    );
  }

  // Pending state
  return (
    <Panel>
      <PanelSection>
        <InlineStack alignBlock="center" alignInline="space-between" gap="200">
          <Box flexGrow="1" display="flex" flexDirection="column" gap="050">
            <Text size="s" weight="medium">
              {title}
            </Text>
            {description != null ? (
              <Text size="xs" color="subtle">
                {description}
              </Text>
            ) : null}
          </Box>
          <InlineStack alignBlock="center" gap="075" flexShrink="0">
            <Button size="sm" variant="subtle" onClick={() => onDeny()}>
              Deny
            </Button>

            {!hasDelete ? (
              <ButtonGroup variant="primary" size="small">
                <Button size="sm" variant="primary" onClick={() => onApprove()}>
                  Allow
                </Button>
                <DropdownMenu>
                  <DropdownMenuTriggerButton
                    size="sm"
                    variant="primary"
                    icon="chevron-down"
                    isIconOnly={true}
                    aria-label="More approval options"
                  />
                  <DropdownMenuContent style={{ maxWidth: 320 }}>
                    {showResourceOption ? (
                      <DropdownMenuItem
                        description={buildAlwaysAllowDescription(mutTypes)}
                        onClick={() => {
                          approveResources(mutPaths);
                          onApprove();
                        }}
                      >
                        {buildAlwaysAllowLabel(mutTypes)}
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      isDanger={true}
                      description="Automatically approve all future changes for the rest of this session."
                      onClick={() => {
                        approveAll();
                        onApprove();
                      }}
                    >
                      Allow all changes
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </ButtonGroup>
            ) : (
              <Button size="sm" variant="primary" isDanger={true} onClick={() => onApprove()}>
                Delete
              </Button>
            )}
          </InlineStack>
        </InlineStack>
      </PanelSection>
    </Panel>
  );
}

// Public API re-exports
export { analyzeBashCommand, containsDeleteRequest } from './parse-curl-resources';
export { ApprovedResourcesProvider, useApprovedResources } from './approved-resources-context';
